import {
  ClockRange,
  ClockStep,
  Color,
  HeightReference,
  InterpolationAlgorithm,
} from "cesium";
import { getClimbColorRgba } from "./getClimbColorRgba";
const DEFAULT_TIME_SPEED = 60;

function createCZMLDoc(name, currentTimeIso, startTimeIso, endTimeIso) {
  const documentInterval = `${startTimeIso}/${endTimeIso}`;

  return [
    {
      id: "document",
      name: name,
      version: "1.0",
      clock: {
        // interval: documentInterval,
        currentTime: currentTimeIso,
        multiplier: DEFAULT_TIME_SPEED,
        range: "CLAMPED",
        // clockRange: ClockRange.CLAMPED,
        clockStep: ClockStep.SYSTEM_CLOCK_MULTIPLIER,
      },
    },
  ];
}

export function igcToCzml({
  flightData,
  id,
  pilotName,
  colored = false,
  color,
  trailDuration,
  performanceMode = false
}) {
  const startTimeIso = new Date(flightData[0].epoch * 1000).toISOString();
  const endTimeIso = new Date(
    flightData[flightData.length - 1].epoch * 1000
  ).toISOString();
  const currentTimeIso = new Date(flightData[0].epoch * 1000).toISOString();

  let czmlFileName = colored ? id + "_colored" : id;

  const svgContent = createSvgIcon(pilotName, color);

  const czml = createCZMLDoc(
    czmlFileName,
    currentTimeIso,
    startTimeIso,
    endTimeIso
  );

  const path = createCZMLPath(
    id,
    pilotName,
    flightData,
    svgContent,
    startTimeIso,
    trailDuration,
    performanceMode
  );

  if (!colored) {
    const dataColor = [...color];
    dataColor.push(255);

    path.path = {
      material: {
        polylineOutline: {
          color: {
            rgba: dataColor,
          },
          outlineColor: {
            rgba: dataColor,
          },
          outlineWidth: 5,
        },
      },
      width: 3,
      leadTime: 0,
      trailTime: trailDuration,
      resolution: 1,
    };
  }
  czml.push(path);

  // Colored ("Vario Track") mode: the track itself is now drawn by a single
  // PolylineCollection primitive (see helper/varioTrack.js + CreateDataSource),
  // not thousands of per-fix CZML segment entities. Here we only emit the
  // marker/label; createColorSegments is intentionally no longer used.

  return czml;
}

function createCZMLPath(id, pilotName, flightData, svgContent, startTimeIso, trailDuration, performanceMode = false) {
  const pathList = [];

  const firstEpocTime = flightData[0].epoch;
  flightData.forEach((point, index) => {
    // const timeInSeconds = index; // assuming one data point per minute
    const timeInSeconds = point.epoch - firstEpocTime;
    // dAltitude: terrain-clamped display altitude (helper/terrainClamp.js). Only
    // set where the recorded altitude would draw inside the hill; `altitude`
    // stays the recorded value everywhere it is reported to the user.
    const drawAlt = point.dAltitude != null ? point.dAltitude : point.altitude;
    pathList.push(timeInSeconds, point.lng, point.lat, drawAlt);
  });

  // Sample rate: 1 for normal, 10 for performance mode (reduces description/label updates by 90%)
  const sampleRate = performanceMode ? 10 : 1;
  const [attributeList, labelList] = createAttributeList(flightData, trailDuration, sampleRate);
  // const description = getDescription(start)

  const path = {
    id: id,
    name: pilotName,
    description: attributeList,
    // properties: {
    //   aa: attributeList
    // },
    billboard: {
      image: svgContent,
      width: 20,
      height: 160,
      // scale: 0.5,
      verticalOrigin: "BOTTOM",
      horizontalOrigin: "CENTER",
      eyeOffset: {
        cartesian: [0.0, 0.0, -150],
      },
      // scaleByDistance: {
      //   nearFarScalar: [2000, 2, 20000, 0.5]
      // }
    },
    // model: {
    //   gltf: './models/burnair-model.glb',
    //   scale: 100,
    //   minimumPixelSize: 64,
    //   maximumScale: 20000,
    //   verticalOffset: -50,  // Adjust as needed
    //   //   "nodeTransformations": {
    //   //     "Root": {
    //   //       "rotation": {
    //   //         "unitQuaternion": [0, 0, 1, 0]
    //   //       }
    //   //     }
    //   //   }
    // },
    label: {
      id: "dynamicLabel",
      // text: "1230m", // Should be made dynamic
      text: labelList,
      font: "12pt sans-serif",
      fillColor: {
        rgba: [255, 255, 255, 255],
      },
      outlineColor: {
        rgba: [0, 0, 0, 255],
      },
      outlineWidth: 2,
      heightReference: HeightReference.RELATIVE_TO_GROUND,
      style: "FILL_AND_OUTLINE",
      pixelOffset: {
        cartesian2: [0, -12],
      },
      eyeOffset: {
        cartesian: [0, 0, -100],
      },
      // "disableDepthTestDistance": 1000000000
    },
    position: {
      epoch: startTimeIso,
      cartographicDegrees: pathList,
    },
    // Todo update here for model
    // orientation: {
    //   velocityReference: "#position",
    //   interpolationAlgorithm: InterpolationAlgorithm,
    //   interpolationDegree: 1,
    //   "velocityReferenceYAxis": "-Y"
    // },
  };
  return path;
}

function createAttributeList(flightData, trailDuration, sampleRate = 1) {
  const propertyList = [];
  const labelList = [];

  // Performance optimization: sample every Nth point for large datasets
  for (let i = 0; i < flightData.length - 1; i += sampleRate) {
    const start = flightData[i];
    const end = flightData[Math.min(i + sampleRate, flightData.length - 1)];

    // Calculate end time with trail duration

    const startTime = new Date(start.epoch * 1000);
    const endTime = new Date((end.epoch + trailDuration) * 1000);
    if (isNaN(startTime.getTime()) || isNaN(endTime.getTime())) {
      console.error("Invalid time value detected:", start, end);
      continue;
    }

    const startTimeIso = startTime.toISOString();
    const endTimeIso = endTime.toISOString();
    const documentInterval = `${startTimeIso}/${endTimeIso}`;

    propertyList.push({
      interval: documentInterval,
      string: getDescription(start),
    });

    labelList.push({
      interval: documentInterval,
      string: `${start.altitude.toString()}`,
    });
  }

  return [propertyList, labelList];
}
function createColorSegments(pilotName, flightData, trailDuration) {
  let segmentList = [];
  const positionList = [];
  const colorList = [];
  const propertyList = [];
  for (let i = 0; i < flightData.length - 1; i++) {
    const start = flightData[i];
    const end = flightData[i + 1];
    const verticalSpeed = start.vSpeed; // Use calculated vSpeed
    const colorRgba = getClimbColorRgba(verticalSpeed);

    // Add positions without time offsets for each polyline segment
    const positions = [
      start.lng,
      start.lat,
      start.altitude,
      end.lng,
      end.lat,
      end.altitude,
    ];

    // Calculate end time with trail duration

    const startTime = new Date(start.epoch * 1000);
    const endTime = new Date((end.epoch + trailDuration) * 1000);

    if (isNaN(startTime.getTime()) || isNaN(endTime.getTime())) {
      console.error("Invalid time value detected:", start, end);
      continue;
    }

    const startTimeIso = startTime.toISOString();
    const endTimeIso = endTime.toISOString();
    const documentInterval = `${startTimeIso}/${endTimeIso}`;
    colorList.push({
      interval: documentInterval,
      rgba: colorRgba,
    });

    propertyList.push({
      interval: documentInterval,
      string: "Description with time " + start.epoch.toString(),
    });

    const description = getDescription(start);
    const segment = {
      id: `segment_${i}`,
      name: pilotName,
      availability: documentInterval,
      description: description,
      polyline: {
        positions: {
          cartographicDegrees: positions,
        },
        material: {
          solidColor: {
            color: {
              rgba: colorRgba,
            },
          },
        },
        width: 2, // Change width to 2
      },
    };
    segmentList.push(segment);
  }

  return segmentList;
}

// const timeOptions = {
//   hour: '2-digit',
//   minute: '2-digit',
//   second: '2-digit',
//   hour12: false // Use 24-hour format
// };

const getDescription = (flightItem, date) => {
  const speed = flightItem.vSpeed;
  const alt = flightItem.altitude;

  const time = flightItem.timestamp.toString();
  const timeStr = `${time.slice(0, 2)}:${time.slice(2, 4)}:${time.slice(4, 6)}`;

  // Add + sign for positive vSpeed
  const vSpeedStr = speed >= 0 ? `+${speed}` : speed;

  // Use custom CSS class defined in Cesium's InfoBoxDescription.css
  // Ultra-minimal HTML - just 50 bytes!
  return `<div class="flight-info">${alt}m<br>${vSpeedStr}m/s<br>${timeStr}</div>`;
};

export function createSvgIcon(name, color = [255, 1, 1]) {
  const rgbColor = `rgb(${color.join(",")})`;
  const svgContent = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="320">
  <rect width="40" height="8" style="fill: ${rgbColor};" />
  <rect y="8" width="40" height="252" style="fill: rgba(0,0,0,1);" />
  <text x="-250" y="28" transform="rotate(-90)" style="fill: #FFFFFF;"
  font-family="Roboto, sans-serif" font-size="26px">${name}</text>
  <polyline points="0,260 20,280 40,260 0, 260" style="stroke: ${rgbColor}; fill: ${rgbColor}; stroke-width: 0" /></svg>`;

  return svgToBase64(svgContent);
}

function svgToBase64(svgContent) {
  // Extract inner content of the <svg> element
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgContent, "image/svg+xml");
  const innerContent = new XMLSerializer().serializeToString(
    doc.documentElement
  );

  return `data:image/svg+xml;base64,${btoa(
    unescape(encodeURIComponent(innerContent))
  )}`;
}

// Function to generate a random RGBA color array
function getRandomColor() {
  return [
    Math.floor(Math.random() * 255),
    Math.floor(Math.random() * 255),
    Math.floor(Math.random() * 255),
    255, // alpha channel set to fully opaque
  ];
}
